import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { useTranslation } from 'react-i18next';

/**
 * Single-vendor 1099 generation, from the vendor row.
 *
 * `force` bypasses the threshold gate for a deliberate below-threshold filing
 * or a correction. It does not bypass the corporate exemption or an explicit
 * force-exclude -- gl-api enforces that regardless of this flag.
 */

const currentYear = new Date().getFullYear();
const TAX_YEARS = [currentYear + 1, currentYear, currentYear - 1, currentYear - 2];

interface Props {
  show: boolean;
  saving: boolean;
  vendorName: string;
  onClose: () => void;
  onGenerate: (taxYear: number, force: boolean) => void;
}

const GenerateFormModal: React.FC<Props> = ({ show, saving, vendorName, onClose, onGenerate }) => {
  const { t } = useTranslation('Tax1099Vendors');
  const [taxYear, setTaxYear] = useState(String(currentYear));
  const [force, setForce] = useState(false);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onGenerate(Number(taxYear), force);
  };

  return (
    <Modal show={show} onHide={onClose} backdrop="static" centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{t('generate.title', { vendor: vendorName })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t('generate.tax_year')}</Form.Label>
            <Form.Select value={taxYear} onChange={(event) => setTaxYear(event.target.value)}>
              {TAX_YEARS.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Check
            type="checkbox"
            id="generate-force"
            label={t('generate.force')}
            checked={force}
            onChange={(event) => setForce(event.target.checked)}
          />
          <Form.Text muted>{t('generate.force_help')}</Form.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('generate.cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? t('generate.generating') : t('generate.generate')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default GenerateFormModal;
